// =============================================================================
// screens/CommunitiesScreen.tsx — communities DIRECTORY (Communities tab
// redirect target + root-stack 'Communities').
//
// REDESIGN (triage P1s + reference IMG_5876/5873):
//  - Category chips (Crypto/NFT/DeFi/Metaverse) join the All/Joined/Trending
//    filters in ONE GlassPill row with explicit string labels. The June-build
//    "blank ellipsis chips" (IMG_5859) came from the pre-GlassPill ad-hoc pill
//    styles (zero-width label Text) — those dead styles are now deleted and
//    every chip renders through GlassPill, the single pill implementation.
//  - Token-branded cards: coin-logo circle + name + member AVATAR-STACK
//    (components/discover/AvatarStack; count is real, circles decorative since
//    the list payload has no member avatars and /members 403s for non-members).
//  - Preview modal (IMG_5873): tapping a NON-joined community opens the compact
//    CommunityPreviewModal bottom sheet (Join = POST /communities/:name/join,
//    View = navigate). Joined communities still navigate directly.
//
// MIGRATION (bare RN, P3 COMMUNITIES group): ported off apps/cryb-native
// app/communities.tsx. Swaps: expo-router → react-navigation (useNavigation;
// header options → navigation.setOptions); @expo/vector-icons → Icon shim;
// expo-linear-gradient → react-native-linear-gradient. Services/glass import
// paths unchanged.
// =============================================================================
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  RefreshControl,
  useWindowDimensions,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale,
} from '../constants/theme';
import { GlassPill, GlassPillGroup, GlassCard, GlassButton, GlassSurface } from '../components/glass';
import Asset3D from '../components/ui/Asset3D';
import ScreenHeading from '../components/ui/ScreenHeading';
import { api } from '../services/api';
import ParticleBackground from '../components/ui/ParticleBackground';
import ScreenTip from '../components/ui/ScreenTip';
import { CoinCommunityCard, CommunityPreviewModal, matchesCategory } from '../components/discover';
import type { PreviewTarget, DiscoverCategory } from '../components/discover';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Uniform vertical rhythm between directory cards (token-sized, no magic gaps).
function ListGap() {
  return <View style={{ height: spacing.md }} />;
}

// One clean chip row: view filters + reference categories (IMG_5876), all with
// EXPLICIT string labels rendered by GlassPill (fixes the blank-chip P1).
const FILTERS = ['All', 'Joined', 'Trending', 'Crypto', 'NFT', 'DeFi', 'Metaverse'] as const;
type FilterKey = typeof FILTERS[number];
const CATEGORY_FILTERS: FilterKey[] = ['Crypto', 'NFT', 'DeFi', 'Metaverse'];

interface Community {
  id: string;
  name: string;
  displayName: string;
  description: string;
  avatar?: string;
  memberCount: number;
  joined?: boolean;
  category?: string;
}

export default function CommunitiesScreen() {
  const navigation = useNavigation<Nav>();
  // Safe-area top inset: this screen owns its OWN translucent in-content header
  // (like Home), so the ParticleBackground bleeds full-bleed under the status
  // bar/notch. The header content is padded down by insets.top — no opaque
  // native strip consuming a fixed band above the animated layer.
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const contentMaxWidth = layout === 'desktop' ? 900 : layout === 'tablet' ? 760 : undefined;
  const containerStyle = layout !== 'phone'
    ? { maxWidth: contentMaxWidth, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  // Directory grid: 1 col phone, 2 tablet, 3 desktop. `numColumns`/`key` remount
  // is handled below so a rotation/resize re-lays cleanly.
  const gridColumns = layout === 'desktop' ? 3 : layout === 'tablet' ? 2 : 1;
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterKey>('All');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Preview modal (IMG_5873): tap a NON-joined community → compact join sheet.
  const [preview, setPreview] = useState<Community | null>(null);
  const [previewJoining, setPreviewJoining] = useState(false);

  // Suppress the native header. App.tsx / the root stack register a native
  // "Communities" header, but inside the Tabs navigator (headerShown:false) that
  // strip renders as an OPAQUE bar (headerStyle.backgroundColor = colors.bg)
  // sitting ABOVE the ParticleBackground — the misaligned top gap the client
  // flagged. We remove it and render our own translucent in-content header (see
  // below) so the animated bg is full-bleed to the status bar, matching Home's
  // exact top anatomy (skill §7 spatial consistency, §16.4 familiarity).
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const fetchCommunities = useCallback(async () => {
    setError(null);
    // Backend wraps the list as { data: { items: [...] } }; api() unwraps one
    // level, so the array lives at res.data.items (NOT res.data directly).
    // List items use `icon` (not `avatar`) and `isJoined` (not `joined`).
    // Normalize both so the avatar renders and the Join/Joined state is correct.
    const res = await api<{ items: any[] }>('/communities');
    if (res.success && res.data) {
      const items = Array.isArray(res.data.items) ? res.data.items : [];
      setCommunities(
        items.map((c: any) => ({
          ...c,
          avatar: c.icon ?? c.avatar,
          joined: c.isJoined ?? c.joined ?? false,
        }))
      );
    } else {
      setError(res.error || 'Failed to load communities');
    }
    setLoading(false);
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCommunities();
    setRefreshing(false);
  }, [fetchCommunities]);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  // Refetch on focus so join-state (Join/Joined + member count) stays fresh after
  // joining a community elsewhere (e.g. the detail screen) — fetchCommunities runs
  // only on mount otherwise, leaving the list stale (matches SearchScreen's
  // useFocusEffect pattern).
  useFocusEffect(
    useCallback(() => {
      fetchCommunities();
    }, [fetchCommunities])
  );

  // --- Preview modal actions --------------------------------------------------
  function openCommunity(community: Community) {
    navigation.navigate('Community', { name: community.name });
  }

  function handleCardPress(community: Community) {
    if (community.joined) openCommunity(community);
    else setPreview(community);
  }

  async function handleJoinFromPreview() {
    if (!preview || previewJoining) return;
    setPreviewJoining(true);
    const res = await api(`/communities/${preview.name}/join`, { method: 'POST' });
    setPreviewJoining(false);
    const already = !res.success && /already/i.test(res.error || '');
    if (res.success || already) {
      const bump = res.success ? 1 : 0;
      setCommunities((prev) => prev.map((c) =>
        c.id === preview.id ? { ...c, joined: true, memberCount: (c.memberCount ?? 0) + bump } : c
      ));
      setPreview((p) => (p ? { ...p, joined: true, memberCount: (p.memberCount ?? 0) + bump } : p));
    } else {
      // A real failure (not "already joined") left nothing on screen before — tell
      // the user so a silent no-op can't read as "nothing happened".
      Alert.alert('Could not join', res.error || 'Please try again.');
    }
  }

  const previewTarget: PreviewTarget | null = preview ? {
    key: preview.name,
    title: preview.displayName || preview.name,
    handle: `c/${preview.name}`,
    description: preview.description,
    avatar: preview.avatar,
    memberCount: preview.memberCount ?? 0,
    joined: preview.joined,
  } : null;

  const filtered = (Array.isArray(communities) ? communities : [])
    .filter((c) => {
      if (activeFilter === 'Joined' && !c.joined) return false;
      if (CATEGORY_FILTERS.includes(activeFilter) && !matchesCategory(c, activeFilter as DiscoverCategory)) return false;
      if (search) {
        const q = (search || '').toLowerCase();
        return (
          (c.displayName || c.name || '').toLowerCase().includes(q) ||
          (c.description || '').toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (activeFilter === 'Trending') return (b.memberCount ?? 0) - (a.memberCount ?? 0);
      return 0;
    });

  // Directory card = the SAME token-branded card as the Discover hub
  // (components/discover/CoinCommunityCard — reference IMG_5876 anatomy: coin
  // logo + name + count line up top, avatar stack + member count at the bottom),
  // stretched full-width for the vertical list. Join happens in the preview
  // modal (non-joined tap) — exactly like the Discover carousel.
  function renderCommunityCard({ item }: { item: Community }) {
    // Single column keeps the horizontal gutter; multi-column cells flex evenly
    // inside the columnWrapper (which owns the inter-card gap on wide screens).
    return (
      <View style={gridColumns > 1 ? styles.gridCell : styles.cardWrap}>
        <CoinCommunityCard community={item} onPress={() => handleCardPress(item)} fullWidth color={(item as any).color} />
      </View>
    );
  }

  // Skeleton placeholder row while the directory loads (glass, on-brand — no bare
  // spinner-on-flat-screen "AI look").
  function renderSkeleton() {
    return (
      <View style={[styles.listContent, containerStyle]}>
        {[0, 1, 2, 3, 4].map((i) => (
          <GlassCard key={i} style={styles.skeletonCard} padding={spacing.lg}>
            <View style={styles.skeletonRow}>
              <View style={styles.skeletonAvatar} />
              <View style={styles.skeletonLines}>
                <View style={[styles.skeletonLine, { width: '55%' }]} />
                <View style={[styles.skeletonLine, styles.skeletonLineThin, { width: '35%' }]} />
              </View>
            </View>
          </GlassCard>
        ))}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Living animated background (design-system): replaces the static blobs so
          the directory reads as a deep, premium glass surface, not a flat screen. */}
      <ParticleBackground />

      <ScreenTip
        tipKey="communities"
        title="Communities"
        text="Discover and join communities. Each community has its own feed."
      />

      {/* In-content header (mirrors Home's slim header + reference IMG_5876's
          "Top Cryb Communities" title + subtitle): title/subtitle + a LABELED
          Create affordance live IN the content, padded by the safe-area top
          inset so the particle bg bleeds full-bleed under the notch — no opaque
          native strip. Title is display type (h2/bold), tightened tracking
          (-0.4), size-specific per skill §15. */}
      <View style={[styles.header, { paddingTop: insets.top + spacing.sm }, containerStyle]}>
        {navigation.canGoBack() && (
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            accessibilityRole="button"
            accessibilityLabel="Go back"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={{ marginRight: spacing.sm, marginTop: 2, alignSelf: 'flex-start' }}
          >
            <Ionicons name="chevron-back" size={26} color={colors.ink} />
          </TouchableOpacity>
        )}
        <View style={styles.headerText}>
          <ScreenHeading title="Communities" subtitle="Find your people. Join the conversation." />
        </View>
        {/* Client: "just a plus looks shit" → a clear, LABELED create pill
            (icon + "Create"), glass-translucent, primary-tinted. Same
            navigation.navigate('CreateCommunity') handler as before. */}
        <TouchableOpacity
          onPress={() => navigation.navigate('CreateCommunity')}
          accessibilityRole="button"
          accessibilityLabel="Create community"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          activeOpacity={0.85}
        >
          <GlassSurface
            variant="pill"
            radius={radius.full}
            surfaceColor={colors.primary}
            borderColor={colors.primary}
            sheen={false}
            contentStyle={styles.createPill}
          >
            <Ionicons name="add" size={18} color="#FFFFFF" />
            <Text style={styles.createPillLabel}>Create</Text>
          </GlassSurface>
        </TouchableOpacity>
      </View>

      {/* Search — glass field (leading magnifier + clear affordance), built on the
          shared GlassSurface primitive so it lifts off the bg with real depth. */}
      <View style={[styles.searchWrap, containerStyle]}>
        <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.searchContent}>
          <Ionicons name="search-outline" size={18} color={colors.muted} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search communities..."
            placeholderTextColor={colors.muted}
            value={search}
            onChangeText={setSearch}
            returnKeyType="search"
            autoCorrect={false}
          />
          {search.length > 0 ? (
            <TouchableOpacity
              onPress={() => setSearch('')}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close-circle" size={18} color={colors.faint} />
            </TouchableOpacity>
          ) : null}
        </GlassSurface>
      </View>

      {/* Filter Pills — segmented glass pills (content-sized, never stretched). The
          active pill fills with primary + shadow (strong active state) via GlassPill. */}
      <View style={containerStyle}>
        <GlassPillGroup style={styles.filterRow}>
          {FILTERS.map((f) => (
            <GlassPill
              key={f}
              label={f}
              active={activeFilter === f}
              onPress={() => setActiveFilter(f)}
              accessibilityLabel={`Filter ${f}`}
            />
          ))}
        </GlassPillGroup>
      </View>

      {loading ? (
        renderSkeleton()
      ) : error && filtered.length === 0 ? (
        <View style={styles.emptyWrap}>
          <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.emptyText}>Couldn't load communities</Text>
          <Text style={styles.emptySubtext}>{error}</Text>
          <TouchableOpacity
            onPress={onRefresh}
            accessibilityRole="button"
            accessibilityLabel="Retry loading communities"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={[styles.emptyText, { color: colors.primary, marginTop: spacing.sm }]}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item, index) => item?.id ?? item?.name ?? String(index)}
          renderItem={renderCommunityCard}
          // Multi-column on wide screens; remount when the column count changes.
          numColumns={gridColumns}
          key={`cols-${gridColumns}`}
          columnWrapperStyle={gridColumns > 1 ? styles.gridRow : undefined}
          // Windowing (no removeClippedSubviews — it can blank cells in a grid).
          initialNumToRender={12}
          maxToRenderPerBatch={12}
          windowSize={11}
          ListHeaderComponent={
            filtered.length > 0 ? (
              <Text style={styles.resultsLabel}>
                {activeFilter === 'Joined' ? 'Your communities' : 'Discover'}
                {'  ·  '}
                {filtered.length}
              </Text>
            ) : null
          }
          ItemSeparatorComponent={gridColumns > 1 ? undefined : ListGap}
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                <Asset3D name="community" size={128} style={{ marginBottom: spacing.md }} />
                <Text style={styles.emptyText}>
                  {CATEGORY_FILTERS.includes(activeFilter)
                    ? `No ${activeFilter} communities yet`
                    : 'No communities yet'}
                </Text>
                <Text style={styles.emptySubtext}>
                  {CATEGORY_FILTERS.includes(activeFilter)
                    ? 'Try another category — or be the first to start one.'
                    : 'Be the first to start one.'}
                </Text>
                <GlassButton
                  title="Create community"
                  icon="add-circle-outline"
                  onPress={() => navigation.navigate('CreateCommunity')}
                  fullWidth
                  style={styles.emptyCta}
                />
                <GlassButton
                  title="Explore"
                  icon="compass-outline"
                  variant="secondary"
                  onPress={() => navigation.navigate('Tabs', { screen: 'Discover' })}
                  fullWidth
                  style={styles.emptyCtaTight}
                />
              </GlassCard>
            </View>
          }
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, containerStyle]}
        />
      )}

      {/* Community preview sheet (IMG_5873 pattern): join or peek before entering */}
      <CommunityPreviewModal
        visible={!!preview}
        target={previewTarget}
        joining={previewJoining}
        onClose={() => setPreview(null)}
        onJoin={handleJoinFromPreview}
        onView={() => {
          if (!preview) return;
          const target = preview;
          setPreview(null);
          openCommunity(target);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // In-content translucent header (chrome bleeds over the particle bg, no opaque
  // strip). Title = display type: h2 size/leading + bold weight + tightened
  // tracking (-0.4), per skill §15 (size-specific tracking, tightened for large
  // display text vs the generic 17px native title).
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: layoutTokens.gutter,
    paddingBottom: spacing.xs,
    gap: spacing.md,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    ...typographyScale.h2,
    ...fonts.bold,
    color: colors.ink,
    letterSpacing: -0.4,
  },
  // Reference IMG_5876 pairs the title with a supporting subtitle line — adds
  // density + wayfinding (skill §16.6 hierarchy) without a heavy bar.
  headerSubtitle: {
    ...typographyScale.caption,
    color: colors.ink2,
    marginTop: 2,
  },
  // Labeled Create affordance (replaces the bare "+"): compact glass pill,
  // icon + word so the action reads at a glance.
  createPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    height: 36,
  },
  createPillLabel: {
    ...fonts.semibold,
    fontSize: 14,
    color: '#FFFFFF',
    letterSpacing: 0.1,
  },
  // Search — outer wrapper only positions; the field itself is a GlassSurface.
  searchWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginTop: spacing.sm,
  },
  searchContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    height: 48,
    gap: spacing.sm,
  },
  searchInput: {
    flex: 1,
    ...typographyScale.body,
    color: colors.ink,
    padding: 0,
  },
  filterRow: {
    paddingHorizontal: layoutTokens.gutter,
    paddingVertical: spacing.md,
    gap: spacing.sm,
  },
  // Section label above the results (typographyScale.caption, brand-muted).
  resultsLabel: {
    ...typographyScale.caption,
    ...fonts.bold,
    color: colors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  // NOTE: chips render ONLY through GlassPill; cards ONLY through the shared
  // CoinCommunityCard (one card implementation across Discover + directory).
  cardWrap: {
    marginHorizontal: layoutTokens.gutter,
  },
  // Multi-column grid (tablet/desktop): the row owns the gutter + inter-card gap,
  // each cell flexes to an equal share so cards line up in a clean grid.
  gridRow: {
    paddingHorizontal: layoutTokens.gutter,
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  gridCell: {
    flex: 1,
  },
  listContent: {
    paddingTop: spacing.sm,
    // Clear the floating glass nav (bottomNavInset) so the last card is visible.
    paddingBottom: layoutTokens.bottomNavInset,
    flexGrow: 1,
  },
  // --- Loading skeleton ---
  skeletonCard: {
    marginBottom: spacing.md,
  },
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  skeletonAvatar: {
    width: 46,
    height: 46,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '14',
  },
  skeletonLines: {
    flex: 1,
    gap: spacing.sm,
  },
  skeletonLine: {
    height: 12,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '14',
  },
  skeletonLineThin: {
    height: 10,
    backgroundColor: colors.primary + '0D',
  },
  // --- Empty / error states ---
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: spacing.xxxl + spacing.xxl,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyCta: {
    marginTop: spacing.lg,
  },
  emptyCtaTight: {
    marginTop: spacing.sm,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
});
